import {useCallback, useMemo} from "react";
import { GridColDef } from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";
import { DateTimeFormatter } from "../../common/DateTimeFormatter";
import {MessageTranslKey} from "./MessageTranslKey";
import {IsdsMessageFilterDto, IsdsMessageInfoDto} from "../../client/message/IsdsMessageApiClient";
import {FilterSearchType} from "./MessageSearchPanel";
import {MessageStatusFormatter} from "./MessageStatusFormatter";
import PageableGrid, {PageLoadSize} from "../../common/pagination/PageableGrid";

function buildMessageGridColumns(t: (key: string) => string): GridColDef[] {
    return [
        {
            field: "id",
            headerName: t(MessageTranslKey.ID),
            width: 300
        },
        {
            field: "createTime",
            headerName: t(MessageTranslKey.CREATE_TIME),
            width: 200,
            valueGetter: params => params.value ? new Date(params.value) : undefined,
            valueFormatter: params => DateTimeFormatter.formatDateTime(params.value)
        },
        {
            field: "currentState",
            headerName: t(MessageTranslKey.CURRENT_STATE),
            width: 150,
            valueGetter: params => MessageStatusFormatter.formatMessageStatusStatus(params.row.currentState?.type, t)
        },
        {
            field: "errorCount",
            headerName: t(MessageTranslKey.ERROR_COUNT),
            width: 200,
        },
        {
            field: "recipient",
            headerName: t(MessageTranslKey.RECIPIENT),
            width: 200,
        },
        {
            field: "annotation",
            headerName: t(MessageTranslKey.ANNOTATION),
            width: 600,
        },
    ];
}

export interface MessageGridProps {
    rowsData: IsdsMessageInfoDto[];
    totalMessageCount: number;
    selectedMessageId?: number;

    nextPageToken?: string | null;
    messageFilter: IsdsMessageFilterDto;
    fetchMessages: (filter: IsdsMessageFilterDto, searchType: FilterSearchType) => void;

    onSelectMessage?: (message: IsdsMessageInfoDto) => void;
    onMessageDoubleClick?: (messageId: number) => void;
}

export default function MessageGrid(props: Readonly<MessageGridProps>) {
    const {rowsData, totalMessageCount, selectedMessageId, nextPageToken, messageFilter, fetchMessages, onSelectMessage, onMessageDoubleClick} = props;

    const {t} = useTranslation();
    const columns = useMemo(() => buildMessageGridColumns(t), [t]);

    const handleFetchMore = useCallback(() => {
        if (!nextPageToken) {
            return;
        }

        const nextPageFilter: IsdsMessageFilterDto = {
            ...messageFilter,
            paging: {
                maxPageSize: PageLoadSize.NEXT_PAGE_SIZE,
                pageToken: nextPageToken,
            },
        };

        fetchMessages(nextPageFilter, FilterSearchType.NEXT_PAGE_SEARCH);
    }, [nextPageToken, messageFilter, fetchMessages]);

    return (
        <PageableGrid
            rowsData={rowsData}
            totalRowsCount={totalMessageCount}
            columns={columns}
            onFetchMore={handleFetchMore}
            selectedRowId={selectedMessageId}
            onRowSelect={onSelectMessage}
            onRowDoubleClick={onMessageDoubleClick}
        />
    );
}
